import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  AuthShell,
  AuthField,
  AuthPrimaryButton,
  AuthErrorBanner,
  formatAuthErrorDetail,
} from "@/components/auth/AuthShell";

export default function Login() {
  const { login: googleLogin, loginWithPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";
  const expired = location.state?.reason === "expired";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await loginWithPassword(email.trim(), password);
      toast.success("Welcome back.");
      navigate(from, { replace: true });
    } catch (err) {
      setError(
        formatAuthErrorDetail(err.response?.data?.detail) ||
          "Sign in failed. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Welcome back"
      title="Sign in to Quill."
      subtitle="Pick up where you left off — invoices, splits, and filings, all in one ledger."
      footer={
        <>
          New here?{" "}
          <Link
            to="/signup"
            data-testid="login-signup-link"
            className="text-[#E8B96C] underline underline-offset-4 decoration-[#E8B96C]/40 hover:decoration-[#E8B96C]"
          >
            Create an account
          </Link>
        </>
      }
    >
      <AuthErrorBanner error={error} testId="login-error" />
      {expired && !error && (
        <div
          data-testid="login-session-expired-banner"
          className="mb-5 border border-[#E8B96C]/30 bg-[#E8B96C]/[0.06] px-4 py-3 text-[13px] text-[#F4EBDC] rounded-[4px]"
        >
          Your session expired. Sign in again and we'll take you back to where you were.
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-5">
        <AuthField
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="you@studio.com"
          autoComplete="email"
          testId="login-email-input"
        />
        <AuthField
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          placeholder="••••••••"
          autoComplete="current-password"
          testId="login-password-input"
          rightSlot={
            <Link
              to="/forgot-password"
              data-testid="login-forgot-link"
              className="text-[11px] uppercase tracking-[0.18em] text-[#A89680] hover:text-[#E8B96C] transition-colors"
            >
              Forgot?
            </Link>
          }
        />
        <AuthPrimaryButton disabled={busy} testId="login-submit-button">
          {busy ? "Signing in…" : "Sign in"}
        </AuthPrimaryButton>
      </form>

      <div className="my-7 flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-[#7A6A54]">
        <div className="flex-1 h-px bg-[#E8B96C]/15" />
        or
        <div className="flex-1 h-px bg-[#E8B96C]/15" />
      </div>

      <button
        type="button"
        onClick={googleLogin}
        data-testid="login-google-button"
        className="w-full py-3 flex items-center justify-center gap-3 border border-[#E8B96C]/25 rounded-[4px] text-[#F4EBDC] hover:border-[#E8B96C]/50 hover:bg-[#E8B96C]/[0.04] transition-all text-[14px]"
      >
        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
        Continue with Google
      </button>
    </AuthShell>
  );
}
